/** MultilineChart.js */
import React from 'react';
import * as d3 from 'd3';
import PropTypes from 'prop-types';
import OSHARiskLevelBadge
  from '@avtechsoftware/react-component-library/src/components/common/OSHARiskLevelBadge/OSHARiskLevelBadge';

// Small hook: observes the size of a ref'd element.
const useResizeObserver = (ref) => {
  const [size, setSize] = React.useState({ width: 0, height: 0 });

  React.useEffect(() => {
    if (!ref.current || typeof ResizeObserver === 'undefined') return undefined;
    const el = ref.current;
    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const { width, height } = entry.contentRect;
      setSize((prev) => (prev.width === width && prev.height === height
        ? prev
        : { width, height }));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);

  return size;
};

const DashboardHeatIndex = (props) => {
  const {
    min, max, value, segmentArcs, units,
  } = props;

  const containerRef = React.useRef(null);
  const svgRef = React.useRef(null);
  const { width: containerWidth, height: containerHeight } = useResizeObserver(containerRef);

  // The gauge is a half-circle: full width = 2 * half-height.
  // Fit inside the container while preserving that ratio.
  // Fallback to 225 until the observer reports a size.
  const availableWidth = containerWidth || 225;
  const availableHeight = (containerHeight || 112) * 2; // *2 because we only draw the top half
  const svgWidth = Math.max(50, Math.min(availableWidth, availableHeight));
  const svgHeight = svgWidth; // internal coordinate system; only top half is shown

  // Scale factor so widths/fonts that were tuned for 225px still look right. // ADDED
  const scale = svgWidth / 225; // ADDED
  // Extra room below the baseline so the reading text's glyph height /       // ADDED
  // descenders aren't clipped by the SVG's bottom edge. Scales with size     // ADDED
  // so smaller (already-fine) sizes are unaffected.                          // ADDED
  const bottomPadding = 32 * scale * 0.35; // ADDED

  const createArc = (startDegree, endDegree, radius, width) => d3.arc()
    .innerRadius(radius)
    .outerRadius(radius + width)
    .startAngle((Math.PI / 180) * startDegree)
    .endAngle((Math.PI / 180) * endDegree);

  const attachArcWithLabelAndColor = (selection,
    startDegrees,
    endDegrees,
    radius,
    width,
    color,
    label,
    selectionWidth,
    selectionHeight) => {
    selection.append('path')
      .attr('transform', `translate(${selectionWidth / 2},${selectionHeight / 2})`)
      .attr('d', createArc(startDegrees, endDegrees, radius, width)).attr('fill', color)
      .style('stroke-width', 'none');
  };

  const createGradient = (d, arc, arcFill) => {
    const miniArcs = [];
    const angleExtent = d.endAngle - d.startAngle;
    const noOfArcs = angleExtent * 75;
    const colour = d3.scaleLinear()
      .domain([0, noOfArcs * 0.40, noOfArcs * 0.50, noOfArcs * 0.75, noOfArcs * 0.9, noOfArcs])
      .range(['#1F8CE1', '#00c27e', '#FEFD00', '#ff9600', '#f20000'])
      .interpolate(d3.interpolateRgb.gamma(2.2));
    const miniArcAngle = angleExtent / noOfArcs;
    for (let j = 0; j < noOfArcs; j += 1) {
      const miniArc = {};
      miniArc.startAngle = d.startAngle + (miniArcAngle * j);
      miniArc.endAngle = miniArc.startAngle + miniArcAngle + 0.01;
      miniArc.endAngle = miniArc.endAngle > d.endAngle ? d.endAngle : miniArc.endAngle;
      miniArcs.push(miniArc);
    }
    arcFill.selectAll('.mini-arc')
      .data(miniArcs)
      .enter()
      .append('g')
      .append('path')
      .attr('class', 'arc')
      .attr('d', arc)
      .style('fill', (a, i) => colour(i + 0))
      .style('stroke-width', 'none');
    return 'none';
  };

  const attachArcText = (selection, text, fontWeight, fontSize, textX, textY) => {
    selection.append('text').attr('transform', `translate(${textX},${textY})`).attr('text-anchor', 'middle')
      .text(() => `${text}`)
      .style('font-size', fontSize)
      .attr('font-weight', fontWeight);
  };

  React.useEffect(() => {
    if (!svgWidth || !svgHeight) return;

    let data = [180];
    const radius = svgHeight * 0.4;
    const hole = radius * 0.80;

    const svgEl = d3.select(svgRef.current);
    svgEl.selectAll('*').remove();

    const drawnHeight = svgHeight / 2 + bottomPadding; // ADDED
    svgEl // CHANGED (was: svgEl.attr('width'/'height') with raw pixels)
      .attr('viewBox', `0 0 ${svgWidth} ${drawnHeight}`) // ADDED
      .attr('preserveAspectRatio', 'xMidYMid meet') // ADDED
      .style('width', '100%') // ADDED
      .style('height', '100%') // ADDED
      .style('overflow', 'visible'); // ADDED

    const arcFill = svgEl.append('g')
      .attr('transform', `translate(${svgWidth / 2},${svgHeight / 2})`);
    const arcOutline = svgEl.append('g')
      .attr('transform', `translate(${svgWidth / 2},${svgHeight / 2})`);

    const coloredArc = d3.arc()
      .innerRadius(hole)
      .outerRadius(radius - 12 * scale);

    const pie = d3.pie()
      .startAngle(-Math.PI / 2)
      .endAngle(Math.PI / 2)
      .sort(null)
      .value((d) => d);
    data = pie(data);

    arcOutline.selectAll('.arc')
      .data(data)
      .enter()
      .append('path')
      .attr('class', 'arc')
      .attr('d', coloredArc)
      .style('fill', (d) => createGradient(d, coloredArc, arcFill))
      .style('stroke-width', 'none');

    let computedValue = value;
    if (value >= max) computedValue = max - 1;
    if (value < min) computedValue = min;

    const valueAngle = (180 / (max - min)) * (computedValue - min) - 90;

    segmentArcs.forEach((segmentArc) => {
      attachArcWithLabelAndColor(
        svgEl,
        segmentArc.startDegrees,
        segmentArc.endDegrees,
        radius - 12 * scale,
        26 * scale,
        segmentArc.color,
        'test',
        svgWidth,
        svgHeight,
      );
    });
    attachArcWithLabelAndColor(svgEl, valueAngle - 1, valueAngle + 1, hole - 12 * scale, 20 * scale, 'black', '', svgWidth, svgHeight);
    attachArcText(svgEl, 'Feels Like', 300, 20 * scale, svgWidth / 2, svgHeight / 2 - 34 * scale);
    attachArcText(svgEl, `${value}°${units}`, 700, 32 * scale, svgWidth / 2, svgHeight / 2);
  }, [svgWidth, svgHeight, value, min, max, units, segmentArcs, scale, bottomPadding]);

  return (
    <div
      ref={containerRef}
      style={{
        width: '90%',
        height: '75%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <div className="row">
        <div className="col text-center">
          <svg ref={svgRef} style={{ overflow: 'visible' }} />
        </div>
      </div>
      <div className="row mt-4">
        <div className="col text-center">
          <OSHARiskLevelBadge value={value} />
        </div>
      </div>
    </div>
  );
};

DashboardHeatIndex.defaultProps = {
  segmentArcs: [
    { startDegrees: -90, endDegrees: -1, color: '#B3B3B3' },
    { startDegrees: 1, endDegrees: 44, color: '#B3B3B3' },
    { startDegrees: 46, endDegrees: 90, color: '#B3B3B3' },
  ],
};

DashboardHeatIndex.propTypes = {
  value: PropTypes.number.isRequired,
  units: PropTypes.string.isRequired,
  max: PropTypes.number.isRequired,
  min: PropTypes.number.isRequired,
  segmentArcs: PropTypes.arrayOf(PropTypes.shape(
    { startDegrees: PropTypes.number, endDegrees: PropTypes.number },
  )),
};

export default DashboardHeatIndex;
